/**
 * Which screens a role may open.
 *
 * One table, read by the sidebar (which tabs to draw) and by the redirect
 * guard (which addresses to allow). Keeping both off the same list is the
 * point: hiding a tab is not access control — a typed hash or a stale history
 * entry opens the screen anyway — so the two must never be able to disagree.
 *
 * It mirrors what the API already enforces. The server is the real guard and
 * answers 403 whatever this file says; this is so a cashier is not shown four
 * screens that would only refuse them.
 *
 *   waiter       one screen, the order pad. Signed in for one order.
 *   cashier      takes orders, settles them, keeps the customers book.
 *   storekeeper  the store, and nothing else — no prices, no takings.
 *   manager      everything.
 *
 * A role nobody listed gets everything, deliberately: a new role added to the
 * server should not silently lose its screens here. It will still be refused
 * by the API on anything it may not touch — the visible failure of being shown
 * a screen and told no is better than the invisible one of a tab that vanished
 * for reasons nobody can find.
 */

/** Everything, for a role with no restriction of its own. */
export const ALL = null;

const ACCESS = {
  waiter: ['/orders'],
  cashier: ['/orders', '/orderlist', '/customers'],
  storekeeper: ['/stock'],
  manager: ALL,
  'superadmin-superadmin': ALL,
};

/** The paths a role may open, or null for "all of them". */
export function allowedPathsFor(role) {
  const key = String(role || '').toLowerCase();
  return Object.prototype.hasOwnProperty.call(ACCESS, key) ? ACCESS[key] : ALL;
}

/** Whether one address is open to a role. */
export function canOpen(role, pathname) {
  const allowed = allowedPathsFor(role);
  if (allowed === ALL) return true;
  // startsWith, not equality: a sub-route such as /orderlist/92 belongs to the
  // tab that owns it.
  return allowed.some((path) => pathname === path || pathname.startsWith(`${path}/`));
}

/**
 * The tabs to draw.
 *
 * Filtered in the order the sidebar lists them, so a restricted role sees its
 * screens in the same places as everyone else.
 */
export function tabsFor(role, tabs) {
  const allowed = allowedPathsFor(role);
  if (allowed === ALL) return tabs;
  return tabs.filter((item) => allowed.indexOf(item.path) !== -1);
}

/**
 * Where a role lands when it signs in, and where it is sent back to.
 *
 * The first screen it is allowed, which is the order pad for most people and
 * the store for a storekeeper. `/orders` for an unrestricted role, exactly as
 * before.
 */
export function homeFor(role) {
  const allowed = allowedPathsFor(role);
  return allowed === ALL ? '/orders' : allowed[0] || '/orders';
}
